/**
 * Frontend Error and Warning Reporter
 * 
 * Captures errors and warnings from:
 * - window.onerror
 * - unhandledrejection
 * - Vue app.config.errorHandler
 * - Vue app.config.warnHandler
 * - console.warn (wrapped)
 * 
 * Applies denylist + 10s throttle, calls reportFrontendEvent (fire-and-forget).
 */

/**
 * Denylist patterns for known framework noise.
 * Start small, expand as needed.
 */
const DENYLIST_PATTERNS = [
	// Vue recursive update warnings
	/maximum recursive updates exceeded/i,
	/You may have an infinite update loop/i,
	
	// Router navigation duplicates
	/navigating to current location/i,
	/Avoided redundant navigation/i,
	
	// Browser extension patterns (common noise)
	/chrome-extension:/i,
	/moz-extension:/i,
	/safari-extension:/i,
	
	// ResizeObserver common noise
	/ResizeObserver loop limit exceeded/i,
	/ResizeObserver loop completed with undelivered notifications/i,
];

/**
 * Simple client-side fingerprint hint for throttling.
 * Approximates server logic but doesn't need to match exactly.
 */
function computeFingerprintHint({ level, kind, name, message, source, stack }) {
	// Simplified normalization (client doesn't need crypto)
	const parts = [
		String(level || ""),
		String(kind || ""),
		String(name || ""),
		String(message || "").substring(0, 100), // first 100 chars
		String(source || "").substring(0, 50),
		String(stack || "").split("\n")[0] || "", // first line only
	];
	
	return parts.join("|");
}

/**
 * Check if an error/warning matches the denylist.
 */
function isDenylisted(message, stack) {
	const text = `${message} ${stack}`;
	return DENYLIST_PATTERNS.some(pattern => pattern.test(text));
}

/**
 * Throttle map: fingerprint hint -> last sent timestamp.
 * Session-scoped (cleared on page reload).
 */
const throttleMap = new Map();
const THROTTLE_WINDOW_MS = 10000; // 10 seconds

/**
 * Check if an event should be throttled.
 */
function isThrottled(fingerprintHint) {
	const lastSent = throttleMap.get(fingerprintHint);
	if (!lastSent) return false;
	
	const elapsed = Date.now() - lastSent;
	return elapsed < THROTTLE_WINDOW_MS;
}

/**
 * Mark an event as sent (for throttling).
 */
function markSent(fingerprintHint) {
	throttleMap.set(fingerprintHint, Date.now());
}

/**
 * Extract browser info from navigator.
 */
function getBrowserInfo() {
	if (typeof navigator === "undefined") return {};
	
	const ua = navigator.userAgent || "";
	
	// Simple browser detection
	let name = "Unknown";
	let version = "";
	
	if (ua.indexOf("Firefox") > -1) {
		name = "Firefox";
		const match = ua.match(/Firefox\/(\d+)/);
		version = match ? match[1] : "";
	} else if (ua.indexOf("Chrome") > -1) {
		name = "Chrome";
		const match = ua.match(/Chrome\/(\d+)/);
		version = match ? match[1] : "";
	} else if (ua.indexOf("Safari") > -1) {
		name = "Safari";
		const match = ua.match(/Version\/(\d+)/);
		version = match ? match[1] : "";
	} else if (ua.indexOf("Edge") > -1 || ua.indexOf("Edg") > -1) {
		name = "Edge";
		const match = ua.match(/Edg\/(\d+)/);
		version = match ? match[1] : "";
	}
	
	return {
		name,
		version,
		platform: navigator.platform || "",
		userAgent: ua,
	};
}

/**
 * Send an event to reportFrontendEvent (fire-and-forget).
 * Never throws, never blocks the UI.
 */
async function sendEvent(callable, { databaseURL, app, release, level, kind, name, message, source, stack, url }) {
	try {
		const payload = {
			databaseURL,
			app,
			release,
			level,
			kind,
			name,
			message,
			source,
			stack,
			url,
			browser: getBrowserInfo(),
		};
		
		// Fire-and-forget: don't await, swallow errors
		callable("reportFrontendEvent")(payload).catch(err => {
			// Swallow reporter failures silently
			console.debug("[ErrorReporter] Failed to send event:", err?.message || err);
		});
	} catch (err) {
		// Never throw into the app
		console.debug("[ErrorReporter] Failed to prepare event:", err?.message || err);
	}
}

/**
 * Process and optionally report an event.
 */
function handleEvent(callable, config, { level, kind, name, message, source, stack, url }) {
	// Apply denylist
	if (isDenylisted(message, stack)) {
		return;
	}
	
	// Compute fingerprint hint for throttling
	const fingerprintHint = computeFingerprintHint({ level, kind, name, message, source, stack });
	
	// Apply throttle
	if (isThrottled(fingerprintHint)) {
		return;
	}
	
	// Mark as sent
	markSent(fingerprintHint);
	
	// Send event
	sendEvent(callable, {
		databaseURL: config.databaseURL,
		app: config.app,
		release: config.release,
		level,
		kind,
		name,
		message,
		source,
		stack,
		url,
	});
}

/**
 * Extract page URL without query/hash (pathname only).
 * Strips PII from query parameters.
 */
function getPageUrl() {
	if (typeof window === "undefined" || !window.location) return "";
	return window.location.pathname || "/";
}

/**
 * Install error and warning hooks.
 * Call this early in app bootstrap (main.js / createApp path).
 * 
 * @param {Function} callable - Firebase callable function factory
 * @param {object} config - { databaseURL, app, release, appPrefix }
 * @param {object} vueApp - Vue app instance (optional, for Vue error/warn handlers)
 */
export function installErrorReporter(callable, config, vueApp) {
	if (!callable || !config?.databaseURL || !config?.app || !config?.release) {
		console.warn("[ErrorReporter] Missing required config, skipping installation.");
		return;
	}
	
	// BLOCKER FIX 1: TEST-only client gate
	// STC/DOA/TEST share Firebase project stc-21-app. Only install/send for TEST tenant.
	const isTestTenant = config.appPrefix === "test" || 
	                     config.databaseURL === "https://stc-21-app-training.firebaseio.com";
	
	if (!isTestTenant) {
		console.debug("[ErrorReporter] Not TEST tenant, skipping installation.");
		return;
	}
	
	// window.onerror
	const originalOnError = window.onerror;
	window.onerror = function(message, source, lineno, colno, error) {
		handleEvent(callable, config, {
			level: "error",
			kind: "onerror",
			name: error?.name || "Error",
			message: String(message || ""),
			source: String(source || ""),
			stack: error?.stack || "",
			url: getPageUrl(), // BLOCKER FIX 3: pathname only
		});
		
		// Call original handler if it exists
		if (originalOnError) {
			return originalOnError.apply(this, arguments);
		}
		
		return false; // Let default handler run
	};
	
	// unhandledrejection
	window.addEventListener("unhandledrejection", event => {
		const reason = event.reason;
		handleEvent(callable, config, {
			level: "error",
			kind: "unhandledrejection",
			name: reason?.name || "UnhandledRejection",
			message: reason?.message || String(reason || ""),
			source: "",
			stack: reason?.stack || "",
			url: getPageUrl(), // BLOCKER FIX 3: pathname only
		});
	});
	
	// Vue error handler
	if (vueApp) {
		const originalErrorHandler = vueApp.config.errorHandler;
		vueApp.config.errorHandler = function(err, instance, info) {
			handleEvent(callable, config, {
				level: "error",
				kind: "vue",
				name: err?.name || "VueError",
				message: err?.message || String(err || ""),
				source: info || "",
				stack: err?.stack || "",
				url: getPageUrl(), // BLOCKER FIX 3: pathname only
			});
			
			// BLOCKER FIX 2: Forward to console.error (Vue default behavior)
			console.error(err);
			
			// Call original handler if it exists
			if (originalErrorHandler) {
				originalErrorHandler.call(this, err, instance, info);
			}
		};
		
		// Vue warn handler
		const originalWarnHandler = vueApp.config.warnHandler;
		// Save unwrapped console.warn BEFORE we wrap it below
		const unwrappedConsoleWarn = console.warn;
		
		vueApp.config.warnHandler = function(msg, instance, trace) {
			handleEvent(callable, config, {
				level: "warning",
				kind: "vue",
				name: "VueWarning",
				message: String(msg || ""),
				source: "",
				stack: trace || "",
				url: getPageUrl(), // BLOCKER FIX 3: pathname only
			});
			
			// BLOCKER FIX 2: Forward to unwrapped console.warn (Vue default behavior)
			// Use unwrapped to avoid double-reporting when we wrap console.warn below
			unwrappedConsoleWarn.call(console, `[Vue warn]: ${msg}`, trace || "");
			
			// Call original handler if it exists
			if (originalWarnHandler) {
				originalWarnHandler.call(this, msg, instance, trace);
			}
		};
	}
	
	// Wrap console.warn
	const originalConsoleWarn = console.warn;
	console.warn = function(...args) {
		const message = args.map(arg => String(arg)).join(" ");
		
		handleEvent(callable, config, {
			level: "warning",
			kind: "console",
			name: "ConsoleWarning",
			message,
			source: "",
			stack: new Error().stack || "",
			url: getPageUrl(), // BLOCKER FIX 3: pathname only
		});
		
		// Call original console.warn
		originalConsoleWarn.apply(console, args);
	};
	
	console.log("[ErrorReporter] Installed error and warning hooks (TEST tenant only).");
}
